// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedTime from "@insite/client-framework/Common/Utilities/getLocalizedTime";
import translate from "@insite/client-framework/Translate";
import { BillToModel, WarehouseModel } from "@insite/client-framework/Types/ApiModels";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import { BillingAddressInfoDisplayStyles } from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/BillingAddressInfoDisplay";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CheckoutMoveToStep from "@pace/App/Components/Checkout/CheckoutMoveToStep";
import SectionTitle from "@pace/App/Components/Common/SectionTitle";
import { CHECKOUT_STEPS } from "@pace/App/Widgets/CheckoutStepper/CheckoutStepper.helper";
import { StyledBox } from "@pace/XDL/Utilities";
import React from "react";
import { css } from "styled-components";

interface OwnProps {
    pickUpDate: Date | null;
    location: WarehouseModel;
    billTo: BillToModel;
    onEditLocation: () => void;
    onEditBillTo: () => void;
    extendedStyles?: PickUpLocationInfoDisplayStyles;
    // XNG-Changes
    deliverAnytime: boolean;
    notes?: string;
}

export interface PickUpLocationInfoDisplayStyles {
    container?: GridContainerProps;
    pickUpDateGridItem?: GridItemProps;
    pickUpDateHeadingText?: TypographyPresentationProps;
    pickUpDateText?: TypographyPresentationProps;
    locationAddressGridItem?: GridItemProps;
    billingAddressGridItem?: GridItemProps;
    billingAddress?: BillingAddressInfoDisplayStyles;
    // XNG-Changes
    section?: InjectableCss;
    sectionTitle?: TypographyPresentationProps;
    value?: TypographyPresentationProps;
    label?: TypographyPresentationProps;
    hr?: InjectableCss;
}

export const pickUpLocationInfoDisplayStyles: PickUpLocationInfoDisplayStyles = {
    container: { gap: 20 },
    pickUpDateGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    pickUpDateHeadingText: { weight: 600 },
    locationAddressGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    billingAddressGridItem: {
        width: [12, 12, 6, 6, 6],
    },
};

const PickUpLocation = ({
    pickUpDate,
    billTo,
    onEditLocation,
    onEditBillTo,
    extendedStyles,
    // XNG-Changes
    location,
    deliverAnytime,
    notes,
    ...otherProps
}: OwnProps) => {
    const styles = mergeToNew(pickUpLocationInfoDisplayStyles, extendedStyles);

    return (
        <>
            <SectionTitle
                title={translate("Will Call")}
                extraChild={<CheckoutMoveToStep moveToStep={CHECKOUT_STEPS.SHIPPING} />}
                border="none"
            />
            <StyledWrapper {...styles.container}>
                <StyledWrapper {...styles.section}>
                    <Typography {...styles.sectionTitle}>{translate("Branch")}</Typography>
                    <StyledBox>
                        <Typography {...styles.value}>
                            {`${location.name} - ${location.description} - ${location.address1} ${location.address2}. ${location.city} ${location.state}, ${location.postalCode}`}{" "}
                            <br />
                            {location.phone}
                        </Typography>
                    </StyledBox>
                </StyledWrapper>
                {/* <PickUpLocationAddressInfoDisplay  {...otherProps} onEdit={onEditLocation} /> */}

                {pickUpDate && (
                    <StyledWrapper {...styles.section}>
                        <Typography {...styles.sectionTitle}>{translate("Requested Pickup Date / Time")}</Typography>
                        <Typography {...styles.value} data-test-selector="requestedPickUpDateValue">
                            <Typography {...styles.label}>{translate("Date")}:</Typography>
                            <LocalizedDateTime
                                dateTime={pickUpDate}
                                // XNG-Changes
                                options={{ year: "numeric", month: "2-digit", day: "2-digit" }}
                            />
                        </Typography>
                        {/** XNG-Changes */}
                        <Typography {...styles.value} data-test-selector="requestedPickUpTimeValue">
                            <Typography {...styles.label}>{translate("Time")}:</Typography>
                            {deliverAnytime
                                ? translate("Anytime")
                                : getLocalizedTime({
                                      dateTime: pickUpDate,
                                      options: {
                                          hour: "numeric",
                                          minute: "numeric",
                                      },
                                  })}
                        </Typography>
                    </StyledWrapper>
                )}
                {/* <GridItem {...styles.billingAddressGridItem}>
                    <BillingAddressInfoDisplay
                        billTo={billTo}
                        onEdit={onEditBillTo}
                        extendedStyles={styles.billingAddress}
                    />
                </GridItem> */}
                {/** XNG-Changes */}
                {notes && (
                    <StyledWrapper {...styles.section} {...{ fullWidth: true }}>
                        <Typography variant="h6">{translate("Special Shipping / Handling Instructions")}</Typography>
                        <Typography {...styles.value}>{notes}</Typography>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </>
    );
};

export default PickUpLocation;
